import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import {
  convertCoordinatesToGeoPoint,
  convertCoordinatesToLatLngLiteral,
  convertGeoPointToLatLngLiteral,
  convertLatLngToCoordinates,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Button, Dropdown } from "@mapmycustomers/ui";

import LocatedEntityAssociation from "@app/component/associations/LocatedEntityAssociation";
import ButtonLink from "@app/component/ButtonLink";
import AddressField from "@app/component/input/Address/AddressField";
import {
  geocodeLocation,
  setConfigurationVisibility,
  setLeadFinderQuery,
} from "@app/component/leadFinder/store/actions";
import { LeadFinderQueryState } from "@app/component/leadFinder/store/LeadFinderState";
import {
  getLeadFinderAddress,
  getLeadFinderEntity,
  getLeadFinderLocation,
  getLeadFinderMode,
} from "@app/component/leadFinder/store/selectors";
import useMap from "@app/component/map/FeaturedMap/context/useMap";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import { showEntityView } from "@app/store/entityView/actions";
import { getPosition } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import locatedToAddress from "@app/util/locatedToAddress";

import SearchRadius from "../SearchRadius";

import styles from "./SearchArea.module.scss";

interface SearchAreaProps {
  address?: Address;
  classes?: {
    dropdown?: string;
    location?: string;
  };
  className?: string;
  compact?: boolean;
  entity?: EntityWithLocation;
  geocodeLocation: typeof geocodeLocation.request;
  location?: string;
  mode: AreaSearchMode;
  onChange?: (newMode?: AreaSearchMode) => void;
  position?: GeolocationPosition;
  query?: Partial<LeadFinderQueryState>;
  setConfigurationVisibility: typeof setConfigurationVisibility;
  setLeadFinderQuery: typeof setLeadFinderQuery;
  showEntityView: typeof showEntityView;
  text?: React.ReactNode;
}

const messages = defineMessages({
  addLocation: {
    id: "leadFinder.searchPopover.searchArea.addLocation",
    defaultMessage: "Please add a location",
    description: "Address validation error",
  },
  addressLabel: {
    id: "leadFinder.searchPopover.searchArea.addressLabel",
    defaultMessage: "Address",
    description: "Address label",
  },
  [AreaSearchMode.ADDRESS]: {
    id: "leadFinder.searchPopover.searchArea.address",
    defaultMessage: "a specific address",
    description: "Your current location label",
  },
  [AreaSearchMode.CURRENT_MAP_VIEW_CENTER]: {
    id: "leadFinder.searchPopover.searchArea.currentView",
    defaultMessage: "your current map view",
    description: "Your current map view label",
  },
  [AreaSearchMode.CURRENT_USER_LOCATION]: {
    id: "leadFinder.searchPopover.searchArea.currentLocation",
    defaultMessage: "your current location",
    description: "Your current location label",
  },
  [AreaSearchMode.ENTITY]: {
    id: "leadFinder.searchPopover.searchArea.entity",
    defaultMessage: "a specific Company or Person",
    description: "Your current location label",
  },
  around: {
    id: "leadFinder.searchPopover.searchArea.around",
    defaultMessage: "around",
    description: "Around label",
  },
  changeRecord: {
    id: "leadFinder.searchPopover.searchArea.changeRecord",
    defaultMessage: "Change Record",
    description: "Change Record button title",
  },
  locationError: {
    id: "leadFinder.searchPopover.searchArea.locationError",
    defaultMessage: "Unable to detect location",
    description: "Unable to detect location label",
  },
  locationErrorHelp: {
    id: "leadFinder.searchPopover.searchArea.locationError.help",
    defaultMessage: "Get help",
    description: "Unable to detect location - Get help button label",
  },
  search: {
    id: "leadFinder.searchPopover.searchArea.text",
    defaultMessage: "Searching for leads",
    description: "Searching for leads label",
  },
  selectRecord: {
    id: "leadFinder.searchPopover.searchArea.selectRecord",
    defaultMessage: "Select Record",
    description: "Select Record button title",
  },
});

const AVAILABLE_MODES = [
  AreaSearchMode.CURRENT_MAP_VIEW_CENTER,
  AreaSearchMode.CURRENT_USER_LOCATION,
  AreaSearchMode.ENTITY,
  AreaSearchMode.ADDRESS,
];

const CURRENT_MODES = [
  AreaSearchMode.CURRENT_MAP_VIEW_CENTER,
  AreaSearchMode.CURRENT_USER_LOCATION,
];

const SearchArea = ({
  address,
  classes,
  className,
  compact = false,
  entity,
  geocodeLocation,
  location,
  mode,
  onChange,
  position,
  setConfigurationVisibility,
  setLeadFinderQuery,
  showEntityView,
  text,
}: SearchAreaProps) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const { map } = useMap();

  const [entityPopupVisible, showEntityPopup, hideEntityPopup] = useBoolean();

  const hasPosition =
    position?.coords?.longitude !== undefined && position?.coords?.latitude !== undefined;
  const showWarning = !hasPosition && mode === AreaSearchMode.CURRENT_USER_LOCATION;
  const showLocation = !!location && !showWarning && CURRENT_MODES.includes(mode);
  const hasAddress = !!address;

  useEffect(() => {
    switch (mode) {
      case AreaSearchMode.CURRENT_MAP_VIEW_CENTER:
        if (map) {
          const center = map.getCenter()!;
          const geoPoint = convertCoordinatesToGeoPoint(convertLatLngToCoordinates(center));

          if (geoPoint?.coordinates) {
            geocodeLocation({
              point: geoPoint.coordinates,
            });
          }
        }
        break;

      case AreaSearchMode.CURRENT_USER_LOCATION:
        if (hasPosition) {
          map?.panTo(convertCoordinatesToLatLngLiteral(position.coords));
          geocodeLocation({
            point: [position.coords.longitude, position.coords.latitude],
          });
        }
        break;
    }
  }, [geocodeLocation, hasPosition, map, mode, position]);

  const handleModeClick = useCallback<NonNullable<MenuProps["onClick"]>>(
    (value) => {
      const newMode = value.key as AreaSearchMode;

      if (newMode !== mode) {
        analytics.clicked(["Location", newMode]);

        if (newMode === AreaSearchMode.ENTITY) {
          showEntityPopup();
        } else {
          hideEntityPopup();
        }

        const query: LeadFinderQueryState = {
          entity: undefined,
          location: undefined,
          mode: newMode,
          point: undefined,
        };
        setLeadFinderQuery(query);

        // Lookup modes which trigger immediate request for a new results
        // Other modes will trigger refresh upon click of the corresponding action button
        if (CURRENT_MODES.includes(newMode)) {
          onChange?.(newMode);
        }
      }
    },
    [analytics, hideEntityPopup, mode, onChange, setLeadFinderQuery, showEntityPopup]
  );

  const handleAddressChange = useCallback(
    (value?: Address) => {
      setLeadFinderQuery({ address: value });
      if (value) {
        onChange?.();
      }
    },
    [onChange, setLeadFinderQuery]
  );

  const handleEntityViewClick = useCallback(() => {
    if (entity) {
      setConfigurationVisibility({ visible: false });

      showEntityView({
        entityId: entity.id,
        entityType: entity.entity,
      });
    }
  }, [entity, showEntityView, setConfigurationVisibility]);

  const handleSelectEntity = useCallback(
    (entity?: EntityWithLocation) => {
      let location;
      let point;

      if (entity) {
        location = getFormattedAddressForUi(locatedToAddress(entity));
        if (entity.geoPoint) {
          map?.panTo(convertGeoPointToLatLngLiteral(entity.geoPoint));
          point = entity.geoPoint.coordinates;
        }
      }

      setLeadFinderQuery({ entity, location, point });

      if (entity?.geoPoint?.coordinates) {
        onChange?.();
      }
    },
    [map, onChange, setLeadFinderQuery]
  );

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.selector}>
        {text ? text : intl.formatMessage(messages.search)}

        <SearchRadius classes={classes} />

        {intl.formatMessage(messages.around)}

        <Dropdown
          menu={{
            className: styles.menu,
            items: AVAILABLE_MODES.map((key) => ({
              key,
              label: intl.formatMessage(messages[key]),
            })),
            onClick: handleModeClick,
            selectedKeys: [mode],
          }}
          placement="bottomRight"
          trigger={["click"]}
        >
          <Button
            className={cn(styles.btn, classes?.dropdown)}
            rightIcon={<FontAwesomeIcon className={styles.arrow} icon={faAngleDown} />}
            type="text"
          >
            {intl.formatMessage(messages[mode])}
          </Button>
        </Dropdown>
      </div>

      {showWarning && (
        <Alert
          action={
            <ButtonLink
              className={styles.helpLink}
              href="https://hs.mapmycustomers.me/en/support/how-to-share-your-location-with-your-web-browser"
              target="_blank"
            >
              {intl.formatMessage(messages.locationErrorHelp)}
            </ButtonLink>
          }
          message={intl.formatMessage(messages.locationError)}
          showIcon
          type="warning"
        />
      )}

      {showLocation && (
        <div className={cn(styles.current, classes?.location)}>
          <span>{location}</span>
        </div>
      )}

      {mode === AreaSearchMode.ENTITY && (
        <div className={cn(styles.entity, { [styles.compact]: compact })}>
          {entity && (
            <div className={styles.entityInfo}>
              <div className={styles.entityName}>
                <ButtonLink onClick={handleEntityViewClick}>{entity.name}</ButtonLink>
              </div>

              <div className={styles.entityAddress}>{location}</div>
            </div>
          )}

          <div>
            <Button disabled={entityPopupVisible} onClick={showEntityPopup}>
              {intl.formatMessage(entity ? messages.changeRecord : messages.selectRecord)}
            </Button>
          </div>

          <LocatedEntityAssociation
            onHide={hideEntityPopup}
            onSelect={handleSelectEntity}
            selectedEntityId={entity?.id}
            visible={entityPopupVisible}
          />
        </div>
      )}

      {mode === AreaSearchMode.ADDRESS && (
        <div className={cn({ [styles.hasFeedback]: !hasAddress })}>
          <AddressField
            allowAutoCompleteClear
            label={intl.formatMessage(messages.addressLabel)}
            labelClassName={styles.addressLabel}
            onChange={handleAddressChange}
            value={address}
          />

          {!hasAddress && (
            <div className={styles.feedback}>{intl.formatMessage(messages.addLocation)}</div>
          )}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  address: getLeadFinderAddress(state),
  entity: getLeadFinderEntity(state),
  location: getLeadFinderLocation(state),
  mode: getLeadFinderMode(state),
  position: getPosition(state),
});

const mapDispatchToProps = {
  geocodeLocation: geocodeLocation.request,
  setConfigurationVisibility,
  setLeadFinderQuery,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchArea);
